import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { X, Save, Search, UserPlus, AlertTriangle, CheckCircle2, ChevronRight, ChevronLeft, CreditCard, Minus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import { format, addDays } from 'date-fns';

export default function AdminBookingWizard({ draftId }) {
  const { drafts, updateDraft, closeDraft, minimiseDraft } = useBookingDraft();
  const { apiFetch, siteAccess, hasLDA } = useAdmin();
  
  const draft = drafts.find(d => d.id === draftId);
  const form = draft?.data;
  
  const [stage, setStage] = useState(1);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Availability state
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    if (!form?.siteKey) {
      updateDraft(draftId, { siteKey: siteAccess[0] });
    }
  }, [siteAccess, form?.siteKey, draftId, updateDraft]);

  useEffect(() => {
    if (!form?.siteKey) return;
    apiFetch(`/services?siteKey=${form.siteKey}`).then(r => r.json()).then(d => setServices(d.services || []));
    apiFetch(`/providers`).then(r => r.json()).then(d => setProviders(d.providers || []));
  }, [apiFetch, form?.siteKey]);

  // Debounced patient search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiFetch(`/patients?search=${encodeURIComponent(searchQuery)}&limit=5`);
        const data = await res.json();
        setSearchResults(data.patients || []);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, apiFetch]);

  // Fetch availability when reaching Stage 3
  useEffect(() => {
    if (stage === 3 && form?.siteKey && form?.serviceId) {
      setLoadingAvailability(true);
      const start = selectedDateFilter;
      const end = format(addDays(new Date(selectedDateFilter), 7), 'yyyy-MM-dd');
      
      let url = `/availability?siteKey=${form.siteKey}&serviceId=${form.serviceId}&startDate=${start}&endDate=${end}`;
      if (form.providerId) url += `&providerId=${form.providerId}`;

      apiFetch(url).then(r => r.json()).then(d => {
        setAvailability(d.days || []);
      }).finally(() => setLoadingAvailability(false));
    }
  }, [stage, form?.siteKey, form?.serviceId, form?.providerId, selectedDateFilter, apiFetch]);

  if (!draft || !form) return null;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (form.isReschedule) {
        const payload = {
          serviceId: form.serviceId,
          providerId: form.providerId,
          confirmedDate: form.confirmedDate,
          confirmedTime: form.confirmedTime,
          duration: services.find(s => s.id === form.serviceId)?.duration || form.duration,
          override: form.overrideCollision || false,
        };
        const res = await apiFetch(`/bookings/${form.bookingId}/status`, { method: 'PUT', body: payload });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || data.error || 'Failed to reschedule');
        toast.success('Booking rescheduled successfully');
        closeDraft(draftId);
      } else {
        const payload = {
          ...form,
          sendPaymentLink: form.paymentStatus === 'unpaid' ? form.sendPaymentLink : false
        };
        
        const res = await apiFetch('/bookings/admin', { method: 'POST', body: payload });
        const data = await res.json();
        
        if (!res.ok) {
          if (res.status === 409 && data.error === 'Slot no longer available') {
             toast('Slot Conflict Detected', {
               description: 'This time slot is taken or out of hours.',
               action: {
                 label: 'Override',
                 onClick: () => {
                   updateDraft(draftId, { overrideCollision: true });
                   handleSubmit();
                 }
               },
               duration: 8000
             });
             setSaving(false);
             return;
          }
          throw new Error(data.message || data.error || 'Failed to create booking');
        }
        
        toast.success('Booking created successfully');
        closeDraft(draftId);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedService = services.find(s => s.id === form.serviceId);

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => minimiseDraft(draftId)} />
      <motion.div 
        initial={{ y: '100%', x: '-50%' }} animate={{ y: 0, x: '-50%' }} exit={{ y: '100%', x: '-50%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-1/2 w-full max-w-2xl bg-white shadow-2xl border border-stone-200 z-50 flex flex-col rounded-t-2xl overflow-hidden h-[85vh]"
      >
        <div className="p-4 border-b border-stone-200 flex items-center justify-between shrink-0 bg-stone-50/50">
          <div>
            <h3 className="text-lg font-playfair font-medium text-stone-900">{form.isReschedule ? 'Reschedule / Edit Booking' : 'New Booking'}</h3>
            <p className="text-xs text-stone-500">Stage {stage} of 3</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => minimiseDraft(draftId)} className="h-8 w-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-50 tooltip-trigger">
              <Minus className="w-4 h-4 text-stone-500" />
            </button>
            <button onClick={() => closeDraft(draftId)} className="h-8 w-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors">
              <X className="w-4 h-4 text-inherit" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {/* STAGE 1: Service */}
          {stage === 1 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-stone-900">Site</label>
                <select value={form.siteKey} onChange={e=>updateDraft(draftId, { siteKey: e.target.value, serviceId: '' })} className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3 focus:border-stone-900 focus:ring-1 focus:ring-stone-900 outline-none transition-shadow">
                  {siteAccess.map(s => <option key={s} value={s}>{s === 'shihab_romeed' ? 'Shihab Romeed (Personal)' : s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-stone-900">Select Service</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map(s => (
                    <div 
                      key={s.id} 
                      onClick={() => updateDraft(draftId, { serviceId: s.id })}
                      className={cn("p-4 border rounded-xl cursor-pointer transition-all", form.serviceId === s.id ? 'border-stone-900 bg-stone-50 ring-1 ring-stone-900' : 'border-stone-200 hover:border-stone-400')}
                    >
                      <h4 className="font-medium text-stone-900">{s.name}</h4>
                      <p className="text-xs text-stone-500 mt-1">Deposit: £{s.depositAmount} • {s.duration} mins</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* STAGE 2: Patient */}
          {stage === 2 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
              {form.patientMode === 'search' && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input 
                      placeholder="Search existing patient by name, email, or phone..." 
                      className="pl-9 h-11 rounded-xl bg-stone-50"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    {searching && <p className="text-xs text-stone-500 text-center">Searching...</p>}
                    {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                      <p className="text-xs text-stone-500 text-center">No patients found.</p>
                    )}
                    {searchResults.map(p => {
                      const isNoShowRisk = p.bookings?.length > 0;
                      const hasOldMedical = !p.medicalQuestionnaires?.length || new Date(p.medicalQuestionnaires[0].completedAt) < new Date(Date.now() - 1000 * 60 * 60 * 24 * 180);
                      
                      return (
                        <div 
                          key={p.id} 
                          onClick={() => updateDraft(draftId, { 
                            patientMode: 'selected', 
                            patientId: p.id, 
                            patient: { firstName: p.firstName, lastName: p.lastName, email: p.email, phone: p.phone },
                            sendMedicalQuestionnaire: hasOldMedical
                          })}
                          className="p-3 border border-stone-200 rounded-lg cursor-pointer hover:border-stone-400 flex items-center justify-between"
                        >
                          <div>
                            <p className="text-sm font-medium text-stone-900">{p.firstName} {p.lastName}</p>
                            <p className="text-xs text-stone-500">{p.email} • {p.phone}</p>
                          </div>
                          <div className="flex gap-2">
                            {isNoShowRisk && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800">No-Show Risk</span>}
                            {hasOldMedical && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">Update Medical</span>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="pt-4 border-t border-stone-100 text-center">
                    <Button variant="outline" onClick={() => updateDraft(draftId, { patientMode: 'new' })} className="gap-2">
                      <UserPlus className="w-4 h-4" /> Create New Patient
                    </Button>
                  </div>
                </div>
              )}

              {(form.patientMode === 'selected' || form.patientMode === 'new') && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-semibold text-stone-900">
                      {form.patientMode === 'selected' ? 'Selected Patient' : 'New Patient Details'}
                    </h4>
                    <button onClick={() => updateDraft(draftId, { patientMode: 'search', patient: { firstName: '', lastName: '', email: '', phone: '' } })} className="text-xs text-stone-500 hover:text-stone-900 underline">
                      Change
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><label className="text-xs font-medium text-stone-700">First Name</label><Input disabled={form.patientMode === 'selected'} value={form.patient.firstName} onChange={e=>updateDraft(draftId, { patient: { ...form.patient, firstName: e.target.value }})} className="h-9" /></div>
                    <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Last Name</label><Input disabled={form.patientMode === 'selected'} value={form.patient.lastName} onChange={e=>updateDraft(draftId, { patient: { ...form.patient, lastName: e.target.value }})} className="h-9" /></div>
                    <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Email</label><Input disabled={form.patientMode === 'selected'} type="email" value={form.patient.email} onChange={e=>updateDraft(draftId, { patient: { ...form.patient, email: e.target.value }})} className="h-9" /></div>
                    <div className="space-y-1"><label className="text-xs font-medium text-stone-700">Phone</label><Input disabled={form.patientMode === 'selected'} value={form.patient.phone} onChange={e=>updateDraft(draftId, { patient: { ...form.patient, phone: e.target.value }})} className="h-9" /></div>
                  </div>

                  {form.sendMedicalQuestionnaire && form.patientMode === 'selected' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                      <div className="text-sm text-amber-900">
                        <p className="font-medium">Medical History out of date</p>
                        <p className="text-xs mt-0.5 opacity-80">We will automatically SMS/Email them a form link upon booking.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* STAGE 3: Schedule & Payment */}
          {stage === 3 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-1">
                  <h4 className="text-sm font-semibold text-stone-900">Provider & Schedule</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-stone-700">Filter Date From</label>
                    <Input type="date" value={selectedDateFilter} onChange={e=>setSelectedDateFilter(e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-stone-700">Filter Provider</label>
                    <select value={form.providerId} onChange={e=>updateDraft(draftId, { providerId: e.target.value, confirmedDate: '', confirmedTime: '' })} className="w-full h-9 rounded-md border border-stone-200 text-sm px-3">
                      <option value="">Any Provider</option>
                      {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>

                {loadingAvailability ? (
                  <div className="py-8 text-center text-stone-500 text-sm animate-pulse">Loading availability...</div>
                ) : (
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 border border-stone-100 rounded-xl p-3 bg-stone-50/50">
                    {availability.length === 0 ? (
                      <p className="text-sm text-stone-500 text-center py-4">No availability found for this service/provider in this date range.</p>
                    ) : availability.map(day => (
                      <div key={day.date} className="space-y-2">
                        <h5 className="text-xs font-semibold text-stone-800 bg-stone-100 px-2 py-1 rounded-md inline-block">{format(new Date(day.date), 'EEEE, do MMM yyyy')}</h5>
                        {day.slots.length === 0 ? (
                          <p className="text-xs text-stone-400 pl-2">No slots available</p>
                        ) : (
                          <div className="flex flex-wrap gap-2 pl-2">
                            {day.slots.map((slot, idx) => {
                              const isSelected = form.confirmedDate === day.date && form.confirmedTime === slot.time && form.providerId === slot.providerId;
                              return (
                                <button
                                  key={`${slot.time}-${slot.providerId}-${idx}`}
                                  onClick={() => updateDraft(draftId, { confirmedDate: day.date, confirmedTime: slot.time, providerId: slot.providerId })}
                                  className={cn(
                                    "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border relative flex items-center justify-center gap-1.5",
                                    isSelected 
                                      ? "bg-stone-900 text-white border-stone-900 shadow-md scale-105" 
                                      : slot.isOptimal 
                                        ? "bg-amber-50 text-amber-900 border-amber-200 hover:border-amber-400 hover:bg-amber-100 shadow-sm"
                                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-400 hover:shadow-sm"
                                  )}
                                  title={slot.isOptimal ? "Optimal Slot (Prevents schedule fragmentation)" : slot.providerName}
                                >
                                  {slot.isOptimal && <Sparkles className={cn("w-3 h-3", isSelected ? "text-amber-400" : "text-amber-500")} />}
                                  <div className="text-center">
                                    {slot.time}
                                    {!form.providerId && <span className="block text-[9px] opacity-70 truncate max-w-[80px]">{slot.providerName}</span>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!form.isReschedule && (
                <div className="space-y-3 mt-8">
                  <h4 className="text-sm font-semibold text-stone-900 border-b pb-1">Payment & Notes</h4>
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-stone-700">Payment Status</label>
                      <select value={form.paymentStatus} onChange={e=>updateDraft(draftId, { paymentStatus: e.target.value })} className="w-full h-9 rounded-md border border-stone-200 text-sm px-3">
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid (Inline)</option>
                        <option value="not_required">Not Required</option>
                      </select>
                    </div>

                    {form.paymentStatus === 'unpaid' && (
                      <label className="flex items-center gap-2 cursor-pointer p-3 border border-stone-200 rounded-lg hover:bg-stone-50">
                        <input type="checkbox" checked={form.sendPaymentLink} onChange={e=>updateDraft(draftId, { sendPaymentLink: e.target.checked })} className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
                        <div className="text-sm">
                          <p className="font-medium text-stone-900">Send Payment Link</p>
                          <p className="text-xs text-stone-500">Instantly text/email a Stripe checkout link for the £{selectedService?.depositAmount} deposit.</p>
                        </div>
                      </label>
                    )}

                    {form.paymentStatus === 'paid' && (
                      <div className="p-3 bg-stone-50 border border-stone-200 rounded-lg space-y-3">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-stone-700">Payment Method</label>
                          <select value={form.paymentMethod || ''} onChange={e=>updateDraft(draftId, { paymentMethod: e.target.value })} className="w-full h-9 rounded-md border border-stone-200 text-sm px-3 bg-white">
                            <option value="">Select Method</option>
                            <option value="card">Card Terminal (In-Clinic)</option>
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-stone-700">Transaction Notes</label>
                          <Input value={form.paymentNotes || ''} onChange={e=>updateDraft(draftId, { paymentNotes: e.target.value })} className="h-9 bg-white" placeholder="e.g. Receipt #1234" />
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-stone-700">Internal Notes</label>
                      <textarea value={form.adminNotes} onChange={e=>updateDraft(draftId, { adminNotes: e.target.value })} rows={2} className="w-full rounded-md border border-stone-200 text-sm px-3 py-2 resize-none focus:ring-1 focus:ring-stone-900 outline-none"></textarea>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>

        <div className="p-4 border-t border-stone-200 bg-stone-50 flex items-center justify-between shrink-0">
          <Button variant="ghost" onClick={() => setStage((form.isReschedule || form.patientId) && stage === 3 ? 1 : Math.max(1, stage - 1))} disabled={stage === 1} className="h-10 text-stone-500">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          
          {stage < 3 ? (
            <Button 
              onClick={() => setStage((form.isReschedule || form.patientId) && stage === 1 ? 3 : stage + 1)} 
              disabled={(stage === 1 && !form.serviceId) || (stage === 2 && form.patientMode === 'search')} 
              className="h-10 bg-stone-900 text-white hover:bg-stone-800"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={saving || (!form.isReschedule && form.paymentStatus === 'paid' && !form.paymentMethod)} 
              className="h-10 bg-stone-900 text-white hover:bg-stone-800 px-6 gap-2"
            >
              {saving ? 'Saving...' : <><CheckCircle2 className="w-4 h-4"/> Confirm</>}
            </Button>
          )}
        </div>
      </motion.div>
    </>
  );
}
